import {
  AllCommunityModule,
  ModuleRegistry,
  colorSchemeDark,
  themeBalham,
  type CellClickedEvent,
  type CellContextMenuEvent,
  type CellFocusedEvent,
  type CellValueChangedEvent,
  type ColumnMovedEvent,
  type ColumnResizedEvent,
  type ColDef,
  type FilterChangedEvent,
  type SortChangedEvent,
  type ICellRendererParams,
  type RowSelectionOptions,
  type ValueGetterParams,
  type ValueSetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ListPlus, Loader2, Plus, RotateCcw, Save, Search, Trash2, Upload, X } from "lucide-react";
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { BooleanCheckboxIcon } from "@/components/BooleanCheckboxIcon";
import { HtmlTableCell } from "@/components/HtmlTableCell";
import { HtmlTableCellEditor } from "@/components/HtmlTableCellEditor";
import { SelectTableCellEditor } from "@/components/SelectTableCellEditor";
import {
  MediaCarouselOverlay,
  type MediaCarouselState,
} from "@/components/MediaCarouselOverlay";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppContext, type DecryptedBlock } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  type MediaItem,
  mediaItemSrc,
  mediaPreviewLooksImage,
  mediaPreviewLooksVideo,
  normalizeRawMediaList,
} from "@/lib/mediaListHelpers";
import {
  getGridCell,
  getTablePayload,
  isRecord,
  matchingBlocksForTable,
  parseBoolean,
  resolveTableView,
  normalizeToCell,
  rowBelongsToTable,
  selectOptionDisplayClassName,
  isHtmlSchemaType,
  isMarkdownSchemaType,
  buildAutofillRowData,
  type GridCell,
  type SchemaField,
  type TableViewSort,
} from "@/lib/tableBlockHelpers";
import { encrypt } from "@/utls/CryptoHelpers";

/** Matches `textarea`, `text area`, `text_area`, etc. */
function isTextareaSchemaType(type: string): boolean {
  return type.replace(/\s+/g, "").replace(/_/g, "").toLowerCase() === "textarea";
}

function parseTagsForSelect(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (raw != null && raw !== "") {
    return [String(raw).trim()].filter(Boolean);
  }
  return [];
}

function formatTagsForSelect(tags: string[]): string {
  return tags.join("\n");
}

function formatCellValueForFind({
  cell,
  type,
}: {
  cell: GridCell;
  type: string;
}): string {
  if (type === "checkbox") {
    const parsed = parseBoolean(cell.value);
    if (parsed === true) return "true";
    if (parsed === false) return "false";
    return "";
  }
  if (type === "select") {
    return formatTagsForSelect(parseTagsForSelect(cell.value));
  }
  if (type === "mediaList") {
    const items = Array.isArray(cell.data)
      ? normalizeRawMediaList(cell.data as unknown[])
      : [];
    return items
      .map((item) => [item.value, item.caption].filter(Boolean).join(" "))
      .join(" ");
  }
  if (isHtmlSchemaType(type)) {
    if (cell.value == null) return "";
    return String(cell.value)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (cell.value == null) return "";
  return String(cell.value);
}

function rowMatchesFindQuery({
  block,
  query,
  schema,
}: {
  block: DecryptedBlock;
  query: string;
  schema: SchemaField[];
}): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  for (const field of schema) {
    const cell = getGridCell(block, field.key);
    if (!cell) continue;
    const text = formatCellValueForFind({ cell, type: field.type });
    if (text.toLowerCase().includes(normalizedQuery)) return true;
  }
  return false;
}

ModuleRegistry.registerModules([AllCommunityModule]);

const gridTheme = themeBalham.withPart(colorSchemeDark);

const TABLE_ROW_HEIGHT = 32;

const MEDIA_TABLE_THUMB_COUNT = 8;

const getFilterStorageKey = (tableId: string) => `ag-grid-filters-${tableId}`;

const saveFilterModel = ({
  tableId,
  filterModel,
}: {
  tableId: string;
  filterModel: Record<string, unknown>;
}) => {
  try {
    localStorage.setItem(getFilterStorageKey(tableId), JSON.stringify(filterModel));
  } catch (error) {
    console.warn("TableViewRoute: failed to save filter model", error);
  }
};

function sortsFromGridApi({
  api,
}: {
  api: {
    getColumnState: () => Array<{
      colId?: string | null;
      sort?: string | null;
      sortIndex?: number | null;
    }>;
  };
}): Record<string, TableViewSort> {
  const sorts: Record<string, TableViewSort> = {};
  for (const col of api.getColumnState()) {
    const colId = col.colId;
    if (!colId || colId === "#") continue;
    if (col.sort !== "asc" && col.sort !== "desc") continue;
    sorts[colId] = {
      sort: col.sort,
      sortIndex: typeof col.sortIndex === "number" ? col.sortIndex : null,
    };
  }
  return sorts;
}

function canonicalSortsKey(sorts: Record<string, TableViewSort> | undefined): string {
  const items = Object.entries(sorts ?? {})
    .filter(([, value]) => value.sort === "asc" || value.sort === "desc")
    .map(([key, value]) => ({
      key,
      sort: value.sort,
      sortIndex: value.sortIndex ?? 0,
    }))
    .sort(
      (left, right) =>
        left.sortIndex - right.sortIndex || left.key.localeCompare(right.key),
    );
  return JSON.stringify(
    items.map(({ key, sort }, index) => ({ key, sort, sortIndex: index })),
  );
}

function sortsEqual({
  a,
  b,
}: {
  a: Record<string, TableViewSort> | undefined;
  b: Record<string, TableViewSort> | undefined;
}): boolean {
  return canonicalSortsKey(a) === canonicalSortsKey(b);
}

function filtersFromGridApi({
  api,
}: {
  api: { getFilterModel: () => Record<string, unknown> };
}): Record<string, unknown> {
  const model = api.getFilterModel() ?? {};
  const filters: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(model)) {
    if (!key || key === "#" || value == null) continue;
    filters[key] = value;
  }
  return filters;
}

function canonicalFiltersKey(filters: Record<string, unknown> | undefined): string {
  if (!filters) return "";
  const keys = Object.keys(filters).sort();
  if (keys.length === 0) return "";
  const normalized: Record<string, unknown> = {};
  for (const key of keys) {
    normalized[key] = filters[key];
  }
  return JSON.stringify(normalized);
}

function filtersEqual({
  a,
  b,
}: {
  a: Record<string, unknown> | undefined;
  b: Record<string, unknown> | undefined;
}): boolean {
  return canonicalFiltersKey(a) === canonicalFiltersKey(b);
}

async function applyGridFilters({
  api,
  filters,
}: {
  api: {
    setFilterModel: (model: Record<string, unknown> | null) => void | Promise<void>;
    setColumnFilterModel: (key: string, model: unknown) => void | Promise<void>;
    onFilterChanged: () => void;
  };
  filters: Record<string, unknown> | undefined;
}) {
  await api.setFilterModel({});
  if (filters) {
    for (const key of Object.keys(filters)) {
      await api.setColumnFilterModel(key, filters[key]);
    }
  }
  api.onFilterChanged();
}

function applyGridSorts({
  api,
  sorts,
}: {
  api: {
    applyColumnState: (params: {
      defaultState: { sort: null };
      state: Array<{
        colId: string;
        sort: "asc" | "desc";
        sortIndex: number | null;
      }>;
    }) => boolean;
  };
  sorts: Record<string, TableViewSort> | undefined;
}) {
  const state = Object.entries(sorts ?? {})
    .filter(
      (entry): entry is [string, TableViewSort & { sort: "asc" | "desc" }] =>
        entry[1].sort === "asc" || entry[1].sort === "desc",
    )
    .map(([colId, value]) => ({
      colId,
      sort: value.sort,
      sortIndex: value.sortIndex,
    }));
  api.applyColumnState({
    state,
    defaultState: { sort: null },
  });
}

const loadFilterModel = (tableId: string): Record<string, unknown> | null => {
  try {
    const stored = localStorage.getItem(getFilterStorageKey(tableId));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    console.warn("TableViewRoute: failed to load filter model", error);
    return null;
  }
};

function MediaTableThumb({
  item,
  onOpen,
}: {
  item: MediaItem;
  onOpen: () => void;
}) {
  const cls =
    "bg-muted ring-border hover:ring-primary/40 flex size-7 shrink-0 cursor-pointer overflow-hidden rounded border ring-1 transition-shadow outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";
  const title =
    item.caption.trim() ? item.caption : item.value;
  const pickProps = {
    type: "button" as const,
    title,
    ["aria-label" as string]: `Open preview: ${title}`,
    onClick: (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOpen();
    },
  };
  if (mediaPreviewLooksVideo(item)) {
    return (
      <button {...pickProps} className={cls}>
        <video
          src={mediaItemSrc(item)}
          className="pointer-events-none size-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
      </button>
    );
  }
  if (mediaPreviewLooksImage(item)) {
    return (
      <button {...pickProps} className={cls}>
        <img
          src={mediaItemSrc(item)}
          alt=""
          className="pointer-events-none size-full object-cover"
        />
      </button>
    );
  }
  return (
    <button
      {...pickProps}
      className={`${cls} items-center justify-center p-0.5 text-center text-[9px] leading-tight break-all`}
    >
      {item.value}
    </button>
  );
}

export function TableViewRoute() {
  const { id: tableId } = useParams<{ id: string }>();
  const { blocks, isLoading, updateDecryptedBlock, addDecryptedBlock, removeDecryptedBlocks } =
    useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();
  const navigate = useNavigate();
  const contextRowIdRef = useRef<string | null>(null);
  const skipPersistCellChangeRef = useRef(false);
  const gridRef = useRef<AgGridReact<DecryptedBlock>>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const findInputRef = useRef<HTMLInputElement>(null);
  const [currentView, setCurrentView] = useState<string | null>(null);
  const [mediaCarousel, setMediaCarousel] = useState<MediaCarouselState | null>(
    null,
  );
  const [isCreatingBlock, setIsCreatingBlock] = useState(false);
  const [selectedRows, setSelectedRows] = useState<DecryptedBlock[]>([]);
  const [isDeletingRows, setIsDeletingRows] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  const [sortsDirty, setSortsDirty] = useState(false);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [isSavingViewState, setIsSavingViewState] = useState(false);
  const suppressViewDirtyRef = useRef(false);

  const rowSelection = useMemo(
    (): RowSelectionOptions<DecryptedBlock> => ({
      mode: "multiRow",
      headerCheckbox: false,
    }),
    [],
  );

  const openMediaCarousel = useCallback(
    ({ items, index }: { items: MediaItem[]; index: number }) => {
      if (items.length === 0) return;
      setMediaCarousel({ items, initialIndex: index });
    },
    [],
  );

  const gridCarouselContext = useMemo(
    () => ({ openMediaCarousel, focusedRowId }),
    [focusedRowId, openMediaCarousel],
  );

  const tableModel = useMemo(() => {
    if (!tableId) return null;
    const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
    if (!tableBlock) return null;
    const tableData = getTablePayload(tableBlock.data);
    if (!tableData) return null;
    const name = tableData.name ?? "";
    const viewedIsDefault = tableData.isDefault === true;
    const rowBlocks = matchingBlocksForTable({
      blocks,
      tableId,
      viewedIsDefault,
    });
    return {
      name,
      schema: tableData.schema,
      viewedIsDefault,
      rowData: rowBlocks,
      defaultView: tableData.defaultView,
      views: tableData.views ?? [],
    };
  }, [blocks, tableId]);

  const htmlColumnKeys = useMemo(
    () =>
      tableModel?.schema
        .filter((field) => isHtmlSchemaType(field.type))
        .map((field) => field.key) ?? [],
    [tableModel?.schema],
  );

  const activeView = useMemo(
    () =>
      tableModel
        ? resolveTableView({
            viewKey: currentView,
            defaultView: tableModel.defaultView,
            views: tableModel.views,
          })
        : null,
    [currentView, tableModel],
  );

  const defaultViewLabel = tableModel?.defaultView?.name?.trim() || "Default";

  const persistBlockEncrypted = useCallback(
    async ({ block }: { block: DecryptedBlock }): Promise<boolean> => {
      const secretKey =
        typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
      if (!secretKey) {
        console.warn(
          "TableViewRoute: no accessKey in localStorage; cannot persist",
        );
        return false;
      }
      const encryptedData = await encrypt(JSON.stringify(block.data), secretKey);
      const res = await fetch(`/api/blocks/${encodeURIComponent(block.id)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      });
      if (!res.ok) {
        console.error("TableViewRoute: persist failed", await res.text());
        return false;
      }
      return true;
    },
    [],
  );

  const ensureSelectFieldOption = useCallback(
    async ({
      fieldKey,
      option,
    }: {
      fieldKey: string;
      option: string;
    }): Promise<boolean> => {
      if (!tableId) return false;
      const trimmed = option.trim();
      if (!trimmed) return false;

      const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
      if (!tableBlock) return false;
      const tableData = getTablePayload(tableBlock.data);
      if (!tableData) return false;

      const fieldIndex = tableData.schema.findIndex((f) => f.key === fieldKey);
      if (fieldIndex < 0) return false;
      const field = tableData.schema[fieldIndex];
      if (field?.type !== "select") return false;

      const existing = field.options ?? [];
      if (existing.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
        return true;
      }

      const nextSchema = tableData.schema.map((f, i) =>
        i === fieldIndex ? { ...f, options: [...existing, trimmed] } : f,
      );
      const rawData = tableBlock.data;
      if (!isRecord(rawData)) return false;
      const nextData = { ...rawData, schema: nextSchema };
      const nextBlock: DecryptedBlock = {
        id: tableBlock.id,
        type: "table",
        data: nextData,
      };
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (ok) {
        updateDecryptedBlock({ block: nextBlock });
      }
      return ok;
    },
    [blocks, persistBlockEncrypted, tableId, updateDecryptedBlock],
  );

  const breadcrumbTableLabel = isLoading
    ? "…"
    : tableModel
      ? tableModel.name || "Table"
      : "Table not found";

  useEffect(() => {
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: breadcrumbTableLabel },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [breadcrumbTableLabel, setBreadcrumbSegments]);

  const closeFind = useCallback(() => {
    setFindOpen(false);
    setFindQuery("");
  }, []);

  useEffect(() => {
    if (!tableModel) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === "f") {
        event.preventDefault();
        setFindOpen(true);
        requestAnimationFrame(() => {
          findInputRef.current?.focus();
          findInputRef.current?.select();
        });
        return;
      }
      if (event.key === "Escape" && findOpen) {
        event.preventDefault();
        closeFind();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeFind, findOpen, tableModel]);

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [findQuery]);

  const isExternalFilterPresent = useCallback(
    () => findQuery.trim().length > 0,
    [findQuery],
  );

  const doesExternalFilterPass = useCallback(
    (node: { data?: DecryptedBlock }) => {
      if (!tableModel?.schema.length || !node.data) return true;
      return rowMatchesFindQuery({
        block: node.data,
        query: findQuery,
        schema: tableModel.schema,
      });
    },
    [findQuery, tableModel?.schema],
  );

  const allColumnDefsMap = useMemo((): Record<string, ColDef<DecryptedBlock>> => {
    const rowNumberCol: ColDef<DecryptedBlock> = {
      headerName: "#",
      colId: "#",
      width: tableModel?.schema.length ? 50 : 56,
      sortable: false,
      filter: false,
      lockPosition: "left",
      suppressMovable: true,
      valueGetter: (p: ValueGetterParams<DecryptedBlock>) =>
        (p.node?.rowIndex ?? 0) + 1,
    };

    if (!tableModel?.schema.length) {
      return { "#": rowNumberCol };
    }

    const schema = tableModel.schema;
    const defs: Record<string, ColDef<DecryptedBlock>> = {
      "#": rowNumberCol,
    };

    for (const field of schema) {
      const { name, key, type, cellEditor, colWidth } = field;
      const editableInline = type !== "checkbox" && type !== "mediaList";
      const colDef: ColDef<DecryptedBlock> = {
        headerName: name,
        editable: editableInline,
        filter: true,
        sortable: true,
        resizable: true,
        width: colWidth || undefined,
        colId: key,
        valueGetter: (p) => {
          if (type === "select") {
            const cell = getGridCell(p.data, key);
            return parseTagsForSelect(cell?.value);
          }
          const cell = getGridCell(p.data, key);
          if (type === "checkbox") {
            return parseBoolean(cell?.value);
          }
          return cell?.value;
        },
        valueSetter: (p: ValueSetterParams<DecryptedBlock>) => {
          const block = p.data;
          if (!block) return false;
          const raw = block.data;
          if (!isRecord(raw)) return false;
          if (!isRecord(raw.data)) {
            raw.data = {} as Record<string, unknown>;
          }
          const row = raw.data as Record<string, unknown>;
          if (type === "select") {
            const tags = Array.isArray(p.newValue)
              ? p.newValue.map((t) => String(t).trim()).filter(Boolean)
              : parseTagsForSelect(p.newValue);
            row[key] =
              tags.length <= 1 ? normalizeToCell(tags[0] ?? "") : normalizeToCell(tags);
            return true;
          }
          if (type === "number") {
            const v = p.newValue;
            let num: unknown =
              typeof v === "string"
                ? v === ""
                  ? undefined
                  : Number(v)
                : v === "" || v === null || v === undefined
                  ? undefined
                  : v;
            if (
              typeof num === "number" &&
              typeof v === "string" &&
              v.trim() !== "" &&
              Number.isNaN(num)
            ) {
              num = undefined;
            }
            const cell = normalizeToCell(row[key]);
            cell.value = num;
            row[key] = cell;
            return true;
          }
          if (type === "date") {
            const v = p.newValue;
            const cell = normalizeToCell(row[key]);
            cell.value =
              v === undefined || v === null || v === ""
                ? undefined
                : String(v).slice(0, 10);
            row[key] = cell;
            return true;
          }
          const cell = normalizeToCell(row[key]);
          cell.value = p.newValue;
          row[key] = cell;
          return true;
        },
      };

      if (type === "checkbox") {
        colDef.cellRenderer = (p: ICellRendererParams<DecryptedBlock>) => {
          const v = p.value;
          if (v !== true && v !== false) return null;
          return (
            <div className="flex h-full min-w-0 items-center overflow-hidden">
              <BooleanCheckboxIcon value={v} />
            </div>
          );
        };
      }

      if (type === "date") {
        colDef.valueFormatter = (p) => {
          if (!p.value) return "";
          try {
            const d = new Date(String(p.value));
            if (Number.isNaN(d.getTime())) return "";
            return d.toISOString().split("T")[0] ?? "";
          } catch {
            return "";
          }
        };
      }

      if (type === "select") {
        colDef.cellEditor = SelectTableCellEditor;
        colDef.cellEditorPopup = true;
        colDef.suppressKeyboardEvent = (params) =>
          params.editing && params.event.key === "Enter";
        colDef.cellEditorParams = {
          options: field.options ?? [],
          fieldKey: key,
          onCreateOption: ensureSelectFieldOption,
        };
        colDef.cellRenderer = (p: ICellRendererParams<DecryptedBlock>) => {
          const tags = parseTagsForSelect(p.value);
          return (
            <div className="flex h-full min-w-0 flex-nowrap items-center gap-1 overflow-hidden leading-none">
              {tags.map((tag) => (
                <div key={tag} className={selectOptionDisplayClassName(tag)}>
                  {tag}
                </div>
              ))}
            </div>
          );
        };
      }

      if (isHtmlSchemaType(type)) {
        colDef.autoHeight = true;
        colDef.cellRenderer = (p: ICellRendererParams<DecryptedBlock>) => {
          const html =
            p.value != null && p.value !== "" ? String(p.value) : "";
          const ctx = p.context as { focusedRowId?: string | null };
          const expanded =
            p.data?.id != null && p.data.id === ctx.focusedRowId;
          return (
            <div
              className={
                expanded
                  ? "flex h-full min-w-0 items-start overflow-hidden"
                  : "flex h-full min-w-0 items-center overflow-hidden"
              }
            >
              <HtmlTableCell html={html} expanded={expanded} />
            </div>
          );
        };
      }

      if (type === "mediaList") {
        colDef.minWidth = 140;
        colDef.cellRenderer = (p: ICellRendererParams<DecryptedBlock>) => {
          const mediaListRaw = getGridCell(p.data, key)?.data;
          const items = Array.isArray(mediaListRaw)
            ? normalizeRawMediaList(mediaListRaw as unknown[])
            : [];
          if (items.length === 0) {
            return (
              <span className="text-muted-foreground text-xs leading-none">—</span>
            );
          }
          const ctx =
            (
              p.context as {
                openMediaCarousel?: ({
                  items,
                  index,
                }: {
                  items: MediaItem[];
                  index: number;
                }) => void;
              }
            ).openMediaCarousel;
          const shown = items.slice(0, MEDIA_TABLE_THUMB_COUNT);
          const more = items.length - shown.length;
          return (
            <div className="flex h-full min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
              {shown.map((item, thumbIndex) => (
                <MediaTableThumb
                  key={`${item.value}-${thumbIndex}`}
                  item={item}
                  onOpen={() =>
                    ctx?.({
                      items,
                      index: thumbIndex,
                    })
                  }
                />
              ))}
              {more > 0 ? (
                <span className="text-muted-foreground px-0.5 text-xs">+{more}</span>
              ) : null}
            </div>
          );
        };
      }

      if (type === "date") {
        colDef.cellEditor = "agDateStringCellEditor";
      } else if (type === "number") {
        colDef.cellEditor = "agNumberCellEditor";
      } else if (isHtmlSchemaType(type)) {
        colDef.cellEditor = HtmlTableCellEditor;
        colDef.cellEditorPopup = true;
        colDef.suppressKeyboardEvent = (params) =>
          params.editing &&
          params.event.key === "Enter" &&
          params.event.shiftKey;
      } else if (
        cellEditor === "largeText" ||
        isTextareaSchemaType(type) ||
        isMarkdownSchemaType(type)
      ) {
        colDef.cellEditor = "agLargeTextCellEditor";
        colDef.cellEditorPopup = true;
      }

      defs[key] = colDef;
    }

    return defs;
  }, [ensureSelectFieldOption, tableModel, openMediaCarousel]);

  const columnDefs = useMemo((): ColDef<DecryptedBlock>[] => {
    if (!tableModel) return [];

    const visibleColumns = ["#"];
    if (activeView?.columns?.length) {
      visibleColumns.push(...activeView.columns);
    } else if (tableModel.schema.length) {
      visibleColumns.push(...tableModel.schema.map((col) => col.key));
    }

    return visibleColumns
      .map((key) => {
        const colDef = allColumnDefsMap[key];
        if (!colDef) return null;
        const sort = activeView?.sorts?.[key];
        return sort ? { ...colDef, ...sort } : colDef;
      })
      .filter((col): col is ColDef<DecryptedBlock> => col != null);
  }, [activeView, allColumnDefsMap, tableModel]);

  const viewFiltersKey = useMemo(() => {
    const viewKey = currentView ?? "default";
    const filtersKey = activeView?.filters
      ? JSON.stringify(activeView.filters)
      : "";
    const columnsKey =
      activeView?.columns?.join(",") ??
      tableModel?.schema.map((field) => field.key).join(",") ??
      "";
    return `${viewKey}|${columnsKey}|${filtersKey}`;
  }, [activeView?.columns, activeView?.filters, currentView, tableModel?.schema]);

  useEffect(() => {
    if (!tableId || !tableModel) return;

    const applyFilters = async () => {
      const gridApi = gridRef.current?.api;
      if (!gridApi) return;

      suppressViewDirtyRef.current = true;
      try {
        if (activeView?.filters) {
          await applyGridFilters({ api: gridApi, filters: activeView.filters });
          return;
        }

        const savedFilterModel = loadFilterModel(tableId);
        if (savedFilterModel && Object.keys(savedFilterModel).length > 0) {
          await applyGridFilters({ api: gridApi, filters: savedFilterModel });
          return;
        }

        await applyGridFilters({ api: gridApi, filters: undefined });
      } finally {
        Promise.resolve().then(() => {
          suppressViewDirtyRef.current = false;
        });
      }
    };

    const timeoutId = setTimeout(() => {
      void applyFilters();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [activeView?.filters, tableId, tableModel, viewFiltersKey]);

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<DecryptedBlock>) => {
      if (event.data?.id) {
        setFocusedRowId(event.data.id);
      }

      const schemaField = tableModel?.schema.find(
        (s) => s.key === event.column?.getColId(),
      );
      if (schemaField?.type !== "checkbox" || !event.data) return;
      const k = schemaField.key;
      const currentValue = parseBoolean(getGridCell(event.data, k)?.value);
      let newValue: unknown;
      if (currentValue === undefined) newValue = true;
      else if (currentValue === true) newValue = false;
      else newValue = undefined;
      event.node.setDataValue(k, newValue);
    },
    [tableModel?.schema],
  );

  const handleCellFocused = useCallback((event: CellFocusedEvent<DecryptedBlock>) => {
    if (event.rowIndex == null) {
      setFocusedRowId(null);
      return;
    }
    const node = event.api.getDisplayedRowAtIndex(event.rowIndex);
    setFocusedRowId(node?.data?.id ?? null);
  }, []);

  useEffect(() => {
    if (htmlColumnKeys.length === 0) return;
    const api = gridRef.current?.api;
    if (!api) return;
    api.refreshCells({ columns: htmlColumnKeys, force: true });
    api.resetRowHeights();
  }, [focusedRowId, htmlColumnKeys]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (focusedRowId == null) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (gridContainerRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest(".ag-popup, .ag-popup-editor, .ag-popup-child")
      ) {
        return;
      }
      setFocusedRowId(null);
      gridRef.current?.api?.clearFocusedCell();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [focusedRowId]);

  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<DecryptedBlock>) => {
      contextRowIdRef.current = event.data?.id ?? null;
      event.event.preventDefault();
    },
    [],
  );

  const openEditForContextRow = useCallback(() => {
    const rowId = contextRowIdRef.current;
    if (!tableId || !rowId) return;
    const viewQuery = currentView
      ? `?view=${encodeURIComponent(currentView)}`
      : "";
    navigate(`/tables/${tableId}/blocks/${rowId}${viewQuery}`);
  }, [currentView, navigate, tableId]);

  const handleAddBlock = useCallback(async () => {
    if (!tableId || !tableModel || isCreatingBlock) return;
    const secretKey =
      typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
    if (!secretKey) {
      console.warn(
        "TableViewRoute: no accessKey in localStorage; cannot create block",
      );
      return;
    }
    const initialData = {
      parent: tableId,
      data: buildAutofillRowData({ schema: tableModel.schema }),
    };
    setIsCreatingBlock(true);
    try {
      const encryptedData = await encrypt(JSON.stringify(initialData), secretKey);
      const res = await fetch("/api/blocks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      });
      if (!res.ok) {
        console.error("TableViewRoute: create block failed", await res.text());
        return;
      }
      const parsed = (await res.json()) as { block?: { _id?: string } };
      const newId = parsed.block?._id;
      if (!newId) {
        console.error("TableViewRoute: create block missing id in response");
        return;
      }
      addDecryptedBlock({
        block: {
          id: newId,
          data: initialData,
        },
      });
      const viewQuery = currentView
        ? `?view=${encodeURIComponent(currentView)}`
        : "";
      navigate(`/tables/${tableId}/blocks/${newId}${viewQuery}`);
    } finally {
      setIsCreatingBlock(false);
    }
  }, [
    addDecryptedBlock,
    currentView,
    isCreatingBlock,
    navigate,
    tableId,
    tableModel,
  ]);

  useEffect(() => {
    if (!tableModel) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "n") {
        event.preventDefault();
        void handleAddBlock();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleAddBlock, tableModel]);

  const openDebugJsonForContextRow = useCallback(() => {
    const rowId = contextRowIdRef.current;
    if (!tableId || !rowId) return;
    navigate(`/blocks/${rowId}`);
  }, [navigate, tableId]);

  const handleDeleteRows = useCallback(
    async ({ rowIds }: { rowIds: string[] }) => {
      if (rowIds.length === 0 || isDeletingRows) return;
      const message =
        rowIds.length === 1
          ? "Are you sure you want to delete this row?"
          : `Are you sure you want to delete ${rowIds.length} rows?`;
      if (!window.confirm(message)) return;

      setIsDeletingRows(true);
      try {
        const results = await Promise.all(
          rowIds.map(async (id) => {
            const res = await fetch(`/api/blocks/${encodeURIComponent(id)}`, {
              method: "DELETE",
            });
            return { id, ok: res.ok };
          }),
        );
        const succeeded = results.filter((r) => r.ok).map((r) => r.id);
        if (succeeded.length > 0) {
          removeDecryptedBlocks({ ids: succeeded });
          gridRef.current?.api?.deselectAll();
          setSelectedRows([]);
        }
        const failed = results.filter((r) => !r.ok);
        if (failed.length > 0) {
          console.error(
            "TableViewRoute: delete failed for",
            failed.map((f) => f.id),
          );
        }
      } finally {
        setIsDeletingRows(false);
      }
    },
    [isDeletingRows, removeDecryptedBlocks],
  );

  const deleteRowsForContextMenu = useCallback(() => {
    const contextId = contextRowIdRef.current;
    const selected = gridRef.current?.api?.getSelectedRows() ?? [];
    let rowIds: string[];
    if (
      contextId &&
      selected.some((row) => row.id === contextId) &&
      selected.length > 1
    ) {
      rowIds = selected.map((row) => row.id);
    } else if (contextId) {
      rowIds = [contextId];
    } else if (selected.length > 0) {
      rowIds = selected.map((row) => row.id);
    } else {
      return;
    }
    void handleDeleteRows({ rowIds });
  }, [handleDeleteRows]);

  const handleSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    setSelectedRows(api.getSelectedRows());
  }, []);

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<DecryptedBlock>) => {
      if (currentView === null && tableId && !activeView?.filters) {
        saveFilterModel({ tableId, filterModel: event.api.getFilterModel() });
      }
      if (suppressViewDirtyRef.current) return;
      if (event.source === "api") return;
      const currentFilters = filtersFromGridApi({ api: event.api });
      setFiltersDirty(!filtersEqual({ a: activeView?.filters, b: currentFilters }));
    },
    [activeView?.filters, currentView, tableId],
  );

  useEffect(() => {
    setSortsDirty(false);
    setFiltersDirty(false);
  }, [currentView, tableId]);

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<DecryptedBlock>) => {
      if (suppressViewDirtyRef.current) return;
      if (event.source && event.source !== "uiColumnSorted") return;
      const currentSorts = sortsFromGridApi({ api: event.api });
      setSortsDirty(!sortsEqual({ a: activeView?.sorts, b: currentSorts }));
    },
    [activeView?.sorts],
  );

  const handleSaveViewState = useCallback(async () => {
    if (!tableId || isSavingViewState) return;
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
    if (!tableBlock) return;
    const tableData = getTablePayload(tableBlock.data);
    if (!tableData) return;

    const nextSorts = sortsFromGridApi({ api: gridApi });
    const sortsValue = Object.keys(nextSorts).length > 0 ? nextSorts : undefined;
    const nextFilters = filtersFromGridApi({ api: gridApi });
    const filtersValue =
      Object.keys(nextFilters).length > 0 ? nextFilters : undefined;

    const rawData = tableBlock.data;
    if (!isRecord(rawData)) return;

    const nextViewState = { sorts: sortsValue, filters: filtersValue };

    let nextData: Record<string, unknown>;
    if (currentView === null) {
      nextData = {
        ...rawData,
        defaultView: {
          ...tableData.defaultView,
          ...nextViewState,
        },
      };
    } else if (tableData.views?.some((view) => view.key === currentView)) {
      nextData = {
        ...rawData,
        views: tableData.views.map((view) =>
          view.key === currentView ? { ...view, ...nextViewState } : view,
        ),
      };
    } else {
      return;
    }

    setIsSavingViewState(true);
    try {
      const nextBlock: DecryptedBlock = {
        id: tableBlock.id,
        type: "table",
        data: nextData,
      };
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (ok) {
        updateDecryptedBlock({ block: nextBlock });
        setSortsDirty(false);
        setFiltersDirty(false);
      }
    } finally {
      setIsSavingViewState(false);
    }
  }, [
    blocks,
    currentView,
    isSavingViewState,
    persistBlockEncrypted,
    tableId,
    updateDecryptedBlock,
  ]);

  const handleResetViewState = useCallback(async () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    suppressViewDirtyRef.current = true;
    try {
      applyGridSorts({ api: gridApi, sorts: activeView?.sorts });
      await applyGridFilters({ api: gridApi, filters: activeView?.filters });
      if (currentView === null && tableId && !activeView?.filters) {
        saveFilterModel({ tableId, filterModel: {} });
      }
      setSortsDirty(false);
      setFiltersDirty(false);
    } finally {
      Promise.resolve().then(() => {
        suppressViewDirtyRef.current = false;
      });
    }
  }, [activeView?.filters, activeView?.sorts, currentView, tableId]);

  const handleColumnResized = useCallback(
    async (event: ColumnResizedEvent<DecryptedBlock>) => {
      if (!event.finished || event.source !== "uiColumnResized" || !tableId) return;

      const resizedColumns = event.columns ?? (event.column ? [event.column] : []);
      if (resizedColumns.length === 0) return;

      const widthByKey = new Map<string, number>();
      for (const col of resizedColumns) {
        const colId = col.getColId();
        if (colId === "#") continue;
        const width = col.getActualWidth();
        if (!Number.isFinite(width) || width <= 0) continue;
        widthByKey.set(colId, Math.round(width));
      }
      if (widthByKey.size === 0) return;

      const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
      if (!tableBlock) return;
      const tableData = getTablePayload(tableBlock.data);
      if (!tableData) return;

      let changed = false;
      const nextSchema = tableData.schema.map((field) => {
        const newWidth = widthByKey.get(field.key);
        if (newWidth === undefined) return field;
        if (field.colWidth === newWidth) return field;
        changed = true;
        return { ...field, colWidth: newWidth };
      });
      if (!changed) return;

      const rawData = tableBlock.data;
      if (!isRecord(rawData)) return;
      const nextBlock: DecryptedBlock = {
        id: tableBlock.id,
        type: "table",
        data: { ...rawData, schema: nextSchema },
      };
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (ok) {
        updateDecryptedBlock({ block: nextBlock });
      }
    },
    [blocks, persistBlockEncrypted, tableId, updateDecryptedBlock],
  );

  const handleColumnMoved = useCallback(
    async (event: ColumnMovedEvent<DecryptedBlock>) => {
      if (!event.finished || event.source !== "uiColumnMoved" || !tableId) return;

      const orderedKeys = event.api
        .getAllDisplayedColumns()
        .map((col) => col.getColId())
        .filter((colId) => colId !== "#");
      if (orderedKeys.length === 0) return;

      const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
      if (!tableBlock) return;
      const tableData = getTablePayload(tableBlock.data);
      if (!tableData) return;

      const byKey = new Map(tableData.schema.map((field) => [field.key, field]));
      const seen = new Set<string>();
      const nextSchema: SchemaField[] = [];
      for (const key of orderedKeys) {
        const field = byKey.get(key);
        if (!field || seen.has(key)) continue;
        seen.add(key);
        nextSchema.push(field);
      }
      for (const field of tableData.schema) {
        if (seen.has(field.key)) continue;
        seen.add(field.key);
        nextSchema.push(field);
      }

      const schemaChanged = nextSchema.some(
        (field, index) => field.key !== tableData.schema[index]?.key,
      );

      const applyColumnOrder = (prev: string[] | undefined): string[] | undefined => {
        if (!prev?.length) return prev;
        const prevSet = new Set(prev);
        const next = orderedKeys.filter((key) => prevSet.has(key));
        for (const key of prev) {
          if (!next.includes(key)) next.push(key);
        }
        if (next.length === prev.length && next.every((key, i) => key === prev[i])) {
          return prev;
        }
        return next;
      };

      let nextDefaultView = tableData.defaultView;
      let nextViews = tableData.views;
      let viewChanged = false;

      if (currentView === null) {
        const nextColumns = applyColumnOrder(tableData.defaultView?.columns);
        if (nextColumns !== tableData.defaultView?.columns && tableData.defaultView) {
          nextDefaultView = { ...tableData.defaultView, columns: nextColumns };
          viewChanged = true;
        }
      } else if (tableData.views?.length) {
        nextViews = tableData.views.map((view) => {
          if (view.key !== currentView) return view;
          const nextColumns = applyColumnOrder(view.columns);
          if (nextColumns === view.columns) return view;
          viewChanged = true;
          return { ...view, columns: nextColumns };
        });
      }

      if (!schemaChanged && !viewChanged) return;

      const rawData = tableBlock.data;
      if (!isRecord(rawData)) return;
      const nextData: Record<string, unknown> = { ...rawData, schema: nextSchema };
      if (viewChanged) {
        if (currentView === null) {
          nextData.defaultView = nextDefaultView;
        } else {
          nextData.views = nextViews;
        }
      }

      const nextBlock: DecryptedBlock = {
        id: tableBlock.id,
        type: "table",
        data: nextData,
      };
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (ok) {
        updateDecryptedBlock({ block: nextBlock });
      }
    },
    [blocks, currentView, persistBlockEncrypted, tableId, updateDecryptedBlock],
  );

  const handleCellValueChanged = useCallback(
    async (e: CellValueChangedEvent<DecryptedBlock>) => {
      if (skipPersistCellChangeRef.current) return;
      const colId = e.column.getColId();
      const rowBlock = e.data;
      if (!rowBlock?.id || colId === "#" || !tableId || !tableModel?.schema.length)
        return;
      if (
        !rowBelongsToTable({
          rowBlock,
          tableId,
          viewedIsDefault: tableModel.viewedIsDefault,
        })
      ) {
        return;
      }

      const updated: DecryptedBlock = {
        id: rowBlock.id,
        type: rowBlock.type,
        data: structuredClone(rowBlock.data),
      };
      const ok = await persistBlockEncrypted({ block: updated });
      if (ok) {
        updateDecryptedBlock({ block: updated });
        return;
      }
      skipPersistCellChangeRef.current = true;
      e.node.setDataValue(colId, e.oldValue);
      Promise.resolve().then(() => {
        skipPersistCellChangeRef.current = false;
      });
    },
    [
      persistBlockEncrypted,
      tableId,
      tableModel?.schema.length,
      tableModel?.viewedIsDefault,
      updateDecryptedBlock,
    ],
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {!isLoading && tableModel && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 px-2 py-2">
          <Button
            type="button"
            size="sm"
            variant={currentView === null ? "default" : "secondary"}
            onClick={() => setCurrentView(null)}
          >
            {defaultViewLabel}
          </Button>
          {tableModel.views.map((view) => (
            <Button
              key={view.key}
              type="button"
              size="sm"
              variant={currentView === view.key ? "default" : "secondary"}
              onClick={() => setCurrentView(view.key)}
            >
              {view.name}
            </Button>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {findOpen && (
              <div className="flex items-center gap-1.5">
                <Search className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <Input
                  ref={findInputRef}
                  value={findQuery}
                  onChange={(event) => setFindQuery(event.target.value)}
                  placeholder="Find in table…"
                  aria-label="Find in table"
                  className="h-8 w-56"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      closeFind();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Close find"
                  onClick={closeFind}
                >
                  <X className="size-4" aria-hidden />
                </Button>
              </div>
            )}
            {selectedRows.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={isDeletingRows}
                onClick={() =>
                  void handleDeleteRows({
                    rowIds: selectedRows.map((row) => row.id),
                  })
                }
              >
                {isDeletingRows ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <Trash2 className="size-4" aria-hidden />
                )}
                Delete ({selectedRows.length})
              </Button>
            )}
            {sortsDirty || filtersDirty ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={isSavingViewState}
                  onClick={() => void handleResetViewState()}
                >
                  <RotateCcw className="size-4" aria-hidden />
                  Reset
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={isSavingViewState}
                  onClick={() => void handleSaveViewState()}
                >
                  {isSavingViewState ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Save className="size-4" aria-hidden />
                  )}
                  Save Layout
                </Button>
              </>
            ) : null}
            {activeView?.quickAdd?.extractRegex?.trim() ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  const viewQuery = currentView
                    ? `?view=${encodeURIComponent(currentView)}`
                    : "";
                  navigate(`/tables/${tableId}/quick-add${viewQuery}`);
                }}
              >
                <ListPlus className="size-4" aria-hidden />
                Quick Add
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/tables/${tableId}/import`)}
            >
              <Upload className="size-4" aria-hidden />
              Import
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isCreatingBlock}
              onClick={() => void handleAddBlock()}
            >
              {isCreatingBlock ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="size-4" aria-hidden />
              )}
              Add
            </Button>
          </div>
        </div>
      )}
      <div className="flex min-h-[min(70vh,560px)] flex-1 flex-col">
        {!isLoading && tableModel && (
          <ContextMenu onOpenChange={(open) => !open && (contextRowIdRef.current = null)}>
            <ContextMenuTrigger asChild>
              <div
                ref={gridContainerRef}
                className="mk-table-ag-grid flex min-h-0 flex-1 flex-col [&_.ag-root-wrapper]:min-h-[min(70vh,560px)]"
              >
                <AgGridReact
                  ref={gridRef}
                  theme={gridTheme}
                  rowData={tableModel.rowData}
                  columnDefs={columnDefs}
                  rowSelection={rowSelection}
                  getRowId={({ data }) => data?.id ?? ""}
                  rowHeight={TABLE_ROW_HEIGHT}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    wrapText: false,
                  }}
                  onCellClicked={handleCellClicked}
                  onCellFocused={handleCellFocused}
                  onCellContextMenu={handleCellContextMenu}
                  onCellValueChanged={handleCellValueChanged}
                  onFilterChanged={handleFilterChanged}
                  onSortChanged={handleSortChanged}
                  onColumnResized={handleColumnResized}
                  onColumnMoved={handleColumnMoved}
                  onSelectionChanged={handleSelectionChanged}
                  isExternalFilterPresent={isExternalFilterPresent}
                  doesExternalFilterPass={doesExternalFilterPass}
                  suppressContextMenu
                  context={gridCarouselContext}
                />
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem onSelect={openEditForContextRow}>
                Edit
              </ContextMenuItem>
              <ContextMenuItem onSelect={openDebugJsonForContextRow}>
                View block JSON (debug)
              </ContextMenuItem>
              <ContextMenuItem
                disabled={isDeletingRows}
                variant="destructive"
                onSelect={deleteRowsForContextMenu}
              >
                Delete
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        )}
        {!isLoading && !tableModel && (
          <p className="text-muted-foreground text-sm">
            This table does not exist or could not be loaded.
          </p>
        )}
      </div>
      <MediaCarouselOverlay
        open={mediaCarousel !== null}
        onOpenChange={(open) => {
          if (!open) setMediaCarousel(null);
        }}
        carousel={mediaCarousel}
      />
    </div>
  );
}
